import { useEffect, useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { ArrowLeft, Save, Send, Eye, EyeOff, FileText, Download } from 'lucide-react';
import TargetAudienceSelector from './TargetAudienceSelector';
import RecipientPreview from './RecipientPreview';
import SpecificRecipientPicker from './SpecificRecipientPicker';
import RichTextEditor from './RichTextEditor';

interface CampaignEditorProps {
  campaignId: string | null;
  onClose: () => void;
}

interface TargetFilters {
  status?: string | null;
  source?: string | null;
  specific_emails?: string[];
}

interface Campaign {
  id?: string;
  name: string;
  description: string;
  target_table: string;
  target_filters: TargetFilters;
  subject: string;
  html_body: string;
  brand: string;
  status?: string;
}

const MERGE_FIELDS = [
  { label: '{{name}}', value: '{{name}}' },
  { label: '{{email}}', value: '{{email}}' },
  { label: '{{phone}}', value: '{{phone}}' },
  { label: '{{discord_handle}}', value: '{{discord_handle}}' },
  { label: '{{location_timezone}}', value: '{{location_timezone}}' },
  { label: '{{status}}', value: '{{status}}' },
  { label: '{{calendly_scheduled_at}}', value: '{{calendly_scheduled_at}}' },
  { label: '{{interview_question}}', value: '{{interview_question}}' },
  { label: '{{vibe_score}}', value: '{{vibe_score}}' },
  { label: '{{source}}', value: '{{source}}' },
];

export default function CampaignEditor({ campaignId, onClose }: CampaignEditorProps) {
  const { getAdminToken } = useAdminAuth();
  const [loading, setLoading] = useState(!!campaignId);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showRecipientPreview, setShowRecipientPreview] = useState(false);
  const [adminToken, setAdminToken] = useState<string>('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [formData, setFormData] = useState<Campaign>({
    name: '',
    description: '',
    target_table: 'game_dev_applications',
    target_filters: { status: null, source: null, specific_emails: [] },
    subject: '',
    html_body: '',
    brand: 'W.F.P. Entertainment',
  });

  useEffect(() => {
    const fetchToken = async () => {
      const token = await getAdminToken();
      if (token) {
        setAdminToken(token);
      }
    };
    fetchToken();
  }, []);

  useEffect(() => {
    if (campaignId && campaignId !== 'new') {
      loadCampaign();
    }
  }, [campaignId]);

  useEffect(() => {
    if (adminToken) {
      fetchTemplates();
    }
  }, [adminToken]);

  const fetchTemplates = async () => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/templates-admin`,
        {
          headers: {
            'X-Admin-Token': token,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const loadCampaign = async () => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-admin/${campaignId}`,
        {
          headers: {
            'X-Admin-Token': token,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load campaign');
      }

      const data = await response.json();
      setFormData(data);
    } catch (error) {
      console.error('Error loading campaign:', error);
      alert('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.html_body) {
      alert('Please fill in all required fields: Name, Subject, and HTML Body');
      return;
    }

    try {
      setSaving(true);
      const token = await getAdminToken();
      if (!token) return;

      const url = campaignId && campaignId !== 'new'
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-admin/${campaignId}`
        : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-admin`;

      const response = await fetch(url, {
        method: campaignId && campaignId !== 'new' ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': token,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save campaign');
      }

      alert('Campaign saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert(`Failed to save campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleQueueSend = async () => {
    if (!campaignId || campaignId === 'new') {
      alert('Please save the campaign first before queuing');
      return;
    }

    const confirmMessage = `Are you sure you want to queue this campaign for sending?\n\nThis action cannot be undone.`;
    if (!confirm(confirmMessage)) return;

    try {
      setSaving(true);
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaigns-queue`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Token': token,
          },
          body: JSON.stringify({ campaign_id: campaignId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to queue campaign');
      }

      const result = await response.json();
      alert(`Campaign queued successfully! ${result.queued_recipients} emails will be sent.`);
      onClose();
    } catch (error) {
      console.error('Error queuing campaign:', error);
      alert(`Failed to queue campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const getPreviewHtml = () => {
    let html = formData.html_body;
    html = html.replace(/{{\s*name\s*}}/gi, 'Alex Johnson');
    html = html.replace(/{{\s*email\s*}}/gi, 'alex@example.com');
    html = html.replace(/{{\s*phone\s*}}/gi, '+1 (555) 123-4567');
    html = html.replace(/{{\s*discord_handle\s*}}/gi, '@alexdev');
    html = html.replace(/{{\s*location_timezone\s*}}/gi, 'EST (UTC-5)');
    html = html.replace(/{{\s*status\s*}}/gi, 'Scheduled');
    html = html.replace(/{{\s*calendly_scheduled_at\s*}}/gi, 'December 15, 2025 at 2:00 PM');
    html = html.replace(/{{\s*interview_question\s*}}/gi, 'What tools do you use for game development?');
    html = html.replace(/{{\s*vibe_score\s*}}/gi, '8');
    html = html.replace(/{{\s*source\s*}}/gi, 'Website Signup');
    html = html.replace(/{{\s*unsubscribe_url\s*}}/gi, 'https://example.com/unsubscribe');

    const bodyContent = `
      <div style="font-family: Segoe UI, Arial, sans-serif; font-size: 16px; line-height: 1.8; color: #E5E7EB;">
        ${html}
      </div>
    `;

    const currentYear = new Date().getFullYear();
    const brand = formData.brand || 'W.F.P. Entertainment';
    const subject = formData.subject || 'Email Preview';

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=Blinker:wght@700;800&display=swap" rel="stylesheet">
  <title>${subject}</title>
  <style>
    body { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
    a { text-decoration: underline; }
  </style>
</head>
<body style="margin: 0; padding: 0; background: #FFFFFF;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF;">
    <tr>
      <td align="center" style="padding: 32px 16px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width: 900px; background: #111214; border-radius: 18px; border-collapse: separate; box-shadow: 0 4px 20px rgba(0,0,0,0.5);">
          <tr>
            <td style="padding: 36px 24px 16px 24px;">
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td align="center">
                    <div style="font-family: 'Blinker', Segoe UI, Arial, sans-serif; font-weight: 800; font-size: 45px; line-height: 1; color: #FFFFFF;">
                      W.F.P.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding: 0;">
                    <div style="width: 220px; height: 3px; background: #FFFFFF; margin: 0 auto;"></div>
                  </td>
                </tr>
                <tr>
                  <td align="center">
                    <div style="font-family: 'Blinker', Segoe UI, Arial, sans-serif; font-weight: 800; font-size: 28px; line-height: 1.2; color: #FFFFFF;">
                      ENTERTAINMENT
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding: 24px;">
              <div style="font-family: Segoe UI, Arial, sans-serif; font-weight: 700; font-size: 24px; line-height: 1.3; color: #FFFFFF;">
                ${subject}
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding: 0 24px 24px 24px;">
              ${bodyContent}
            </td>
          </tr>
          <tr>
            <td style="padding: 0 24px 24px 24px;">
              <div style="font-family: Segoe UI, Arial, sans-serif; font-size: 14px; line-height: 1.8; color: #E5E7EB;">
                If you want to get in touch, email us at:
                <a href="mailto:apply@yokzona.com" target="_blank" style="color: #FFDE59; text-decoration: underline;">apply@yokzona.com</a>
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding: 0 24px;">
              <div style="margin-top: 12px; border-top: 1px solid #27292B;"></div>
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 24px 32px 24px;">
              <div style="font-family: Segoe UI, Arial, sans-serif; font-size: 12px; line-height: 1.6; color: #9CA3AF;">
                <div>This address is not monitored. Replies are not read.</div>
                <div>&copy; ${currentYear} W.F.P. Studio &middot; <a href="https://yokzona.com" target="_blank" style="color: #FFDE59; text-decoration: underline;">yokzona.com</a></div>
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const handleLoadTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    if (!confirm('Loading a template will replace the current subject and HTML body. Continue?')) {
      return;
    }

    setFormData({
      ...formData,
      subject: template.subject,
      html_body: template.html_body,
    });
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim() || !formData.subject.trim() || !formData.html_body.trim()) {
      alert('Please fill in the template name, subject, and HTML body before saving');
      return;
    }

    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/templates-admin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Token': token,
          },
          body: JSON.stringify({
            name: templateName,
            description: templateDescription,
            subject: formData.subject,
            html_body: formData.html_body,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save template');
      }

      alert('Template saved successfully!');
      setShowSaveTemplate(false);
      setTemplateName('');
      setTemplateDescription('');
      await fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert(`Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const isReadOnly = formData.status === 'sent' || formData.status === 'queued';
  const canQueue = campaignId && campaignId !== 'new' && formData.status === 'draft';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-400 text-lg">Loading campaign...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-yellow-400">
              {campaignId && campaignId !== 'new' ? (isReadOnly ? 'View Campaign' : 'Edit Campaign') : 'New Campaign'}
            </h2>
            {formData.status && (
              <p className="text-gray-400 mt-1">Status: {formData.status.toUpperCase()}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>

          {!isReadOnly && (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-300 transition-colors font-bold disabled:opacity-50"
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              {canQueue && (
                <button
                  onClick={handleQueueSend}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors font-bold disabled:opacity-50"
                >
                  <Send size={16} />
                  Queue Send
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-300 mb-2">
              Campaign Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={isReadOnly}
              className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none disabled:opacity-50"
              placeholder="e.g., Dec 2025 Advance Update"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={isReadOnly}
              rows={3}
              className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none disabled:opacity-50"
              placeholder="Internal description for this campaign"
            />
          </div>

          {!isReadOnly && (
            <div className="bg-black/30 border border-yellow-400/30 rounded-lg p-4">
              <TargetAudienceSelector
                targetTable={formData.target_table}
                targetFilters={formData.target_filters}
                onTargetTableChange={(table) =>
                  setFormData({
                    ...formData,
                    target_table: table,
                    target_filters: { status: null, source: null, specific_emails: [] }
                  })
                }
                onTargetFiltersChange={(filters) =>
                  setFormData({ ...formData, target_filters: filters })
                }
                onPreviewRecipients={() => setShowRecipientPreview(true)}
                adminToken={adminToken}
              />
            </div>
          )}

          {!isReadOnly && (
            <div className="bg-black/30 border border-yellow-400/30 rounded-lg p-4">
              <SpecificRecipientPicker
                targetTable={formData.target_table}
                selectedEmails={formData.target_filters.specific_emails || []}
                onEmailsChange={(emails) =>
                  setFormData({
                    ...formData,
                    target_filters: { ...formData.target_filters, specific_emails: emails }
                  })
                }
              />
            </div>
          )}

          {isReadOnly && (
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-sm font-bold text-gray-300 mb-2">Target Audience</p>
              <p className="text-white">
                {formData.target_table === 'game_dev_applications' ? 'Game Developer Applicants' : 'Newsletter Subscribers'}
              </p>
              {formData.target_filters.status && (
                <p className="text-gray-400 text-sm mt-1">Status: {formData.target_filters.status}</p>
              )}
              {formData.target_filters.source && (
                <p className="text-gray-400 text-sm mt-1">Source: {formData.target_filters.source}</p>
              )}
              {formData.target_filters.specific_emails && formData.target_filters.specific_emails.length > 0 && (
                <p className="text-gray-400 text-sm mt-1">
                  Specific recipients: {formData.target_filters.specific_emails.length}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-300 mb-2">
              Email Subject <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              disabled={isReadOnly}
              maxLength={200}
              className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none disabled:opacity-50"
              placeholder="e.g., Quick update for Advance applicants"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.subject.length}/200 characters</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-300 mb-2">
              Brand Name
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              disabled={isReadOnly}
              className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none disabled:opacity-50"
              placeholder="W.F.P. Entertainment"
            />
          </div>

          {!isReadOnly && templates.length > 0 && (
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">
                Load from Template
              </label>
              <div className="flex gap-2">
                <select
                  onChange={(e) => e.target.value && handleLoadTemplate(e.target.value)}
                  className="flex-1 px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                  value=""
                >
                  <option value="">Select a template...</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  title="Save as Template"
                >
                  <FileText size={16} />
                  Save Template
                </button>
              </div>
            </div>
          )}

          {showSaveTemplate && (
            <div className="bg-gray-800 border border-yellow-400/30 rounded-lg p-4 space-y-3">
              <h4 className="font-bold text-white">Save as Template</h4>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name"
                className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
              />
              <input
                type="text"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Template description (optional)"
                className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors"
                >
                  <Save size={16} />
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveTemplate(false);
                    setTemplateName('');
                    setTemplateDescription('');
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-300 mb-2">
              HTML Body <span className="text-red-400">*</span>
            </label>
            {isReadOnly ? (
              <textarea
                value={formData.html_body}
                disabled
                rows={12}
                className="w-full px-4 py-2 bg-black border border-yellow-400/30 rounded-lg text-white font-mono text-sm focus:border-yellow-400 focus:outline-none disabled:opacity-50"
              />
            ) : (
              <div className="bg-black border border-yellow-400/30 rounded-lg p-4">
                <RichTextEditor
                  value={formData.html_body}
                  onChange={(value) => setFormData({ ...formData, html_body: value })}
                  mergeFields={MERGE_FIELDS}
                  placeholder="<p>Hi {{name}},</p><p>This is an update...</p>"
                />
              </div>
            )}
            {showPreview && (
              <p className="text-xs text-gray-500 italic mt-2">
                Preview shows sample data for all merge fields
              </p>
            )}
          </div>
        </div>

        {showPreview && (
          <div className="lg:sticky lg:top-8 h-fit">
            <div className="bg-gray-900 rounded-lg p-6 shadow-lg border border-yellow-400/30">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">Email Preview</h3>
                <p className="text-sm text-gray-400">This is how your email will appear to recipients</p>
              </div>
              <div className="bg-black rounded-lg p-4 max-h-[600px] overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <RecipientPreview
        isOpen={showRecipientPreview}
        onClose={() => setShowRecipientPreview(false)}
        targetTable={formData.target_table}
        targetFilters={formData.target_filters}
        adminToken={adminToken}
      />
    </div>
  );
}
